import { APP_NATIVE_AD_CLIDS } from '@/constants/general'
import type { PlacementGroup } from '@/modules/adv/ad-service'
import type { getAdProvidersSettings } from '@/modules/adv/get-ad-providers-settings'
import { MINIPLAY_CLID } from '@/modules/main-frame/custom-integrations/miniplay'
import { type AdProvidersSettings, AdProviderTypes } from '@/types'
import type { Config } from '@/utils/clid-config'

export function getCurrentProvidersSettings({
    clid,
    providersSettings,
    clidConfig,
    isGameDistribution,
    isOppoWL,
    isExportOnWidget,
    isPartnerEmbed,
    isNativeAppEnv,
    isGoogleProvidersEnabled,
    isNativeBannersEnabled,
}: {
    clid: string
    providersSettings: ReturnType<typeof getAdProvidersSettings>
    clidConfig: Config
    isGameDistribution: boolean
    isOppoWL: boolean
    isExportOnWidget: boolean
    isPartnerEmbed: boolean
    isNativeAppEnv: boolean
    isGoogleProvidersEnabled: boolean
    isNativeBannersEnabled: boolean
}): PlacementGroup<AdProvidersSettings>[] {
    const APP_NATIVE_PROVIDERS = {
        android: providersSettings.APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        androidTest: providersSettings.APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        ios: providersSettings.APP_NATIVE_IOS_PROVIDERS_SETTINGS,
        iosTest: providersSettings.APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,
    }

    const appNativeWaterfall = APP_NATIVE_AD_CLIDS[clid]
    const appNativeProviders = appNativeWaterfall ? APP_NATIVE_PROVIDERS[appNativeWaterfall] : undefined

    const CUSTOM_PROVIDERS_BY_CLID: Partial<Record<string, AdProvidersSettings[]>> = {
        [MINIPLAY_CLID]: providersSettings.MINIPLAY_PROVIDERS_SETTINGS,
    }

    const GOOGLE_PROVIDER_TYPES = new Set([
        AdProviderTypes.GAM_H5,
        AdProviderTypes.GAM_BANNER_INTERSTITIAL,
        AdProviderTypes.GAM_INSTREAM,
    ])

    function filterGoogleProviders(providers: AdProvidersSettings[]): AdProvidersSettings[] {
        return isGoogleProvidersEnabled
            ? providers
            : providers.filter((p) => !GOOGLE_PROVIDER_TYPES.has(p.type as AdProviderTypes))
    }

    if (isOppoWL) {
        if (clidConfig.adProvidersDisabled) {
            return []
        }

        return [
            {
                placements: ['*'],
                settings: [providersSettings.GOOGLE_AD_SENSE_SETTINGS],
            },
        ]
    }

    if (isGameDistribution) {
        return [
            {
                placements: ['*'],
                settings: [providersSettings.GAME_DISTRIBUTION_PROVIDER_SETTINGS],
            },
        ]
    }
    if (isNativeBannersEnabled && appNativeProviders) {
        return [
            {
                placements: ['*'],
                settings: appNativeProviders,
            },
        ]
    }

    let defaultProviders: AdProvidersSettings[] = []

    if (isNativeAppEnv) {
        defaultProviders = providersSettings.AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE
    } else {
        defaultProviders = providersSettings.AD_PROVIDER_SETTINGS_ON_PORTAL
    }

    let providers: AdProvidersSettings[] = []

    // custom
    if (CUSTOM_PROVIDERS_BY_CLID[clid] && isPartnerEmbed) {
        providers = CUSTOM_PROVIDERS_BY_CLID[clid]
    } else if (isNativeAppEnv && appNativeProviders) {
        // for apps
        providers = appNativeProviders
    } else if (isExportOnWidget) {
        // for isExportOnWidget / partners without custom providers
        providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_GAME_PAGE_ON_WIDGET
    } else {
        providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_GAME_PAGE
    }

    providers = filterGoogleProviders(providers)

    let fallbackProviders: AdProvidersSettings[] = []

    // custom fallback for partners
    if (isPartnerEmbed) {
        fallbackProviders = clidConfig.fallBackProviders ?? providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_PARTNER
    } else if (isNativeAppEnv) {
        fallbackProviders = providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE
    } else {
        fallbackProviders = providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL
    }

    return [
        {
            placements: ['*'],
            settings: filterGoogleProviders(defaultProviders),
        },
        {
            placements: ['interstitial', 'interstitial_midroll', 'interstitial_preroll', 'rewarded'],
            settings: [...providers, ...fallbackProviders],
        },
    ]
}
